import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { canEdit, getUserType } from "../permissions";
import API_BASE from "../config";

const Payments = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const userType = getUserType();
    const allowEdit = canEdit(userType, "payments");
    const [data, setData] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    
    // Initialize state from URL params
    const [search, setSearch] = useState(searchParams.get("search") || "");
    const [statusFilter, setStatusFilter] = useState(searchParams.get("statusFilter") || "");
    const [modeFilter, setModeFilter] = useState(searchParams.get("modeFilter") || "");
    const [paymentForFilter, setPaymentForFilter] = useState(searchParams.get("paymentForFilter") || "");
    const [projectFilter, setProjectFilter] = useState(searchParams.get("projectFilter") || "");
    const [amountSort, setAmountSort] = useState(searchParams.get("amountSort") || "");
    const [dateSort, setDateSort] = useState(searchParams.get("dateSort") || "");
    const [page, setPage] = useState(parseInt(searchParams.get("page"), 10) || 1);
    const perPage = 10;

    // Update URL params when state changes
    useEffect(() => {
        const params = new URLSearchParams();
        if (search) params.set("search", search);
        if (statusFilter) params.set("statusFilter", statusFilter);
        if (modeFilter) params.set("modeFilter", modeFilter);
        if (paymentForFilter) params.set("paymentForFilter", paymentForFilter);
        if (projectFilter) params.set("projectFilter", projectFilter);
        if (amountSort) params.set("amountSort", amountSort);
        if (dateSort) params.set("dateSort", dateSort);
        if (page > 1) params.set("page", page);
        setSearchParams(params, { replace: true });
    }, [search, statusFilter, modeFilter, paymentForFilter, projectFilter, amountSort, dateSort, page, setSearchParams]);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            fetch(`${API_BASE}/api/allrows`, {
                method: "POST",
                headers: { Authorization: "1", "Content-Type": "application/json" },
                body: JSON.stringify({ token: "3232", table: "payments" })
            }).then(r => r.json()),
            fetch(`${API_BASE}/api/allrows`, {
                method: "POST",
                headers: { Authorization: "1", "Content-Type": "application/json" },
                body: JSON.stringify({ token: "3232", table: "projects" })
            }).then(r => r.json())
        ])
            .then(([paymentsJson, projectsJson]) => {
                setLoading(false);
                if (Array.isArray(paymentsJson.data)) setData(paymentsJson.data);
                else setError("Failed to load payments.");

                if (Array.isArray(projectsJson.data)) setProjects(projectsJson.data);
            })
            .catch(() => { setLoading(false); setError("Error fetching data."); });
    }, []);

    // Create a mapping for fast project lookups
    const projectIdMap = {};
    projects.forEach(p => {
        if (p.id) projectIdMap[p.id] = p.name || p.shortform || `Project ${p.id}`;
    });

    const parseDate = (dateStr) => {
        if (!dateStr) return 0;
        const parts = dateStr.includes("/") ? dateStr.split("/") : dateStr.split("-");
        if (parts.length === 3) {
            // Assume format could be DD/MM/YY or DD/MM/YYYY
            let year = parseInt(parts[2], 10);
            if (year < 100) year += 2000;
            return new Date(year, parseInt(parts[1], 10) - 1, parseInt(parts[0], 10)).getTime();
        }
        return new Date(dateStr).getTime() || 0;
    };

    let filtered = data.filter((i) =>
        Object.values(i).join(" ").toLowerCase().includes(search.toLowerCase())
    );
    if (statusFilter) filtered = filtered.filter((i) => i.status === statusFilter);
    if (modeFilter) filtered = filtered.filter((i) => i.payment_mode === modeFilter);
    if (paymentForFilter) filtered = filtered.filter((i) => i.payment_for === paymentForFilter);
    if (projectFilter) filtered = filtered.filter((i) => i.project_id === projectFilter);
    
    if (amountSort === "asc") filtered.sort((a, b) => Number(a.amount) - Number(b.amount));
    if (amountSort === "desc") filtered.sort((a, b) => Number(b.amount) - Number(a.amount));
    if (dateSort === "asc") filtered.sort((a, b) => parseDate(a.payment_collect) - parseDate(b.payment_collect));
    if (dateSort === "desc") filtered.sort((a, b) => parseDate(b.payment_collect) - parseDate(a.payment_collect));

    const totalPages = Math.ceil(filtered.length / perPage);
    const rows = filtered.slice((page - 1) * perPage, page * perPage);

    const fmt = (v) => {
        const n = Number(v);
        return isNaN(n) || n === 0 ? "—" : "₹" + n.toLocaleString("en-IN");
    };

    const paymentModes = [...new Set(data.map((d) => d.payment_mode).filter(Boolean))];
    const paymentFors = [...new Set(data.map((d) => d.payment_for).filter(Boolean))];

    return (
        <>
            <style>{`
        .pm-page { display:flex; flex-direction:column; gap:20px; }
        .pm-header { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; }
        .pm-title { font-size:22px; font-weight:700; color:#0f172a; }
        .pm-count { font-size:13px; color:#64748b; margin-top:2px; }
        .pm-filters { background:#fff; padding:16px; border-radius:14px; border:1px solid #e2e8f0; box-shadow:0 1px 3px rgba(0,0,0,.04); display:flex; gap:12px; flex-wrap:wrap; align-items:center; }
        .pm-filters input,.pm-filters select { padding:10px 14px; border-radius:10px; border:1px solid #e2e8f0; outline:none; min-width:150px; font-size:13px; font-family:'Inter',sans-serif; color:#334155; background:#f8fafc; transition:all .2s; }
        .pm-filters input { flex:1; min-width:200px; }
        .pm-filters input:focus,.pm-filters select:focus { border-color:#93c5fd; box-shadow:0 0 0 3px rgba(59,130,246,.08); background:#fff; }
        .pm-wrap { background:#fff; border-radius:14px; border:1px solid #e2e8f0; box-shadow:0 1px 3px rgba(0,0,0,.04); overflow-x:auto; }
        .pm-table { width:100%; border-collapse:collapse; min-width:1000px; }
        .pm-table th,.pm-table td { padding:13px 14px; font-size:13px; text-align:left; border-bottom:1px solid #f1f5f9; }
        .pm-table th { background:#f8fafc; color:#64748b; font-weight:600; font-size:11px; text-transform:uppercase; letter-spacing:.5px; white-space:nowrap; position:sticky; top:0; }
        .pm-table tbody tr { transition:background .15s; }
        .pm-table tbody tr:hover { background:#f8fafc; }
        .pm-table td { color:#334155; font-weight:500; }
        .pm-cust { display:flex; flex-direction:column; gap:1px; }
        .pm-cust-name { font-weight:600; color:#0f172a; text-transform:capitalize; }
        .pm-cust-id { font-size:11px; color:#94a3b8; }
        .pm-amt { font-weight:700; color:#0f172a; font-variant-numeric:tabular-nums; }
        .pm-badge { display:inline-block; padding:4px 10px; border-radius:20px; font-size:11px; font-weight:600; white-space:nowrap; }
        .pm-badge-confirmed { background:#ecfdf5; color:#059669; border:1px solid #a7f3d0; }
        .pm-badge-pending { background:#fffbeb; color:#d97706; border:1px solid #fcd34d; }
        .pm-badge-na { background:#f8fafc; color:#94a3b8; border:1px solid #e2e8f0; }
        .pm-unit { font-weight:600; color:#2563eb; }
        .pm-date { color:#64748b; font-size:12px; white-space:nowrap; }
        .pm-edit { padding:6px 14px; border:1px solid #e2e8f0; border-radius:8px; background:#f8fafc; color:#2563eb; cursor:pointer; font-size:12px; font-weight:600; font-family:'Inter',sans-serif; transition:all .2s; white-space:nowrap; }
        .pm-edit:hover { background:#eff6ff; border-color:#93c5fd; box-shadow:0 2px 8px rgba(37,99,235,.12); }
        .pm-view { padding:6px 14px; border:1px solid #e2e8f0; border-radius:8px; background:#f8fafc; color:#059669; cursor:pointer; font-size:12px; font-weight:600; font-family:'Inter',sans-serif; transition:all .2s; white-space:nowrap; }
        .pm-view:hover { background:#ecfdf5; border-color:#86efac; box-shadow:0 2px 8px rgba(5,150,105,.12); }
        .pm-create { padding:10px 18px; border:none; border-radius:10px; background:#2563eb; color:#fff; cursor:pointer; font-size:14px; font-weight:600; font-family:'Inter',sans-serif; transition:all .2s; white-space:nowrap; display: flex; align-items: center; gap: 8px; box-shadow:0 4px 12px rgba(37,99,235,.2); }
        .pm-create:hover { background:#1d4ed8; box-shadow:0 6px 16px rgba(37,99,235,.3); transform:translateY(-1px); }
        .pm-act-group { display:flex; gap:6px; }
        .pm-pag { display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; }
        .pm-pag-info { font-size:13px; color:#64748b; }
        .pm-pag-btns { display:flex; align-items:center; gap:6px; }
        .pm-pag-btns button { padding:8px 14px; border-radius:8px; border:1px solid #e2e8f0; background:#fff; font-size:13px; font-weight:500; color:#334155; cursor:pointer; transition:all .2s; font-family:'Inter',sans-serif; }
        .pm-pag-btns button:hover:not(:disabled) { background:#f1f5f9; }
        .pm-pag-btns button:disabled { opacity:.4; cursor:not-allowed; }
        .pm-pag-btns span { font-size:13px; color:#64748b; padding:0 6px; }
        .pm-load { display:flex; flex-direction:column; align-items:center; padding:60px 20px; gap:14px; }
        .pm-spin { width:36px; height:36px; border:3px solid #e2e8f0; border-top-color:#2563eb; border-radius:50%; animation:pm-sp .7s linear infinite; }
        @keyframes pm-sp { to{transform:rotate(360deg)} }
        .pm-load-t { font-size:14px; color:#64748b; }
        .pm-err { padding:16px; background:#fef2f2; border:1px solid #fecaca; border-radius:12px; color:#dc2626; font-size:14px; text-align:center; }
        .pm-empty { padding:40px 20px; text-align:center; color:#94a3b8; font-size:14px; }
        .pm-clear-btn { padding:10px 16px; border:1px solid #e2e8f0; border-radius:10px; background:#fff; color:#475569; font-size:13px; font-weight:600; cursor:pointer; font-family:'Inter',sans-serif; transition:all .2s; height: 100%; white-space: nowrap; }
        .pm-clear-btn:hover { background:#f1f5f9; color:#0f172a; border-color:#cbd5e1; }
        @media(max-width:640px){
          .pm-filters{flex-direction:column;align-items:stretch;}
          .pm-filters input,.pm-filters select, .pm-clear-btn{min-width:100%;}
          .pm-pag{flex-direction:column;align-items:stretch;text-align:center;}
          .pm-pag-btns{justify-content:center;}
        }
      `}</style>
      
            <div className="pm-page">
                <div className="pm-header">
                    <div>
                        <h2 className="pm-title">💰 Payments</h2>
                        <div className="pm-count">{loading ? "Loading..." : `${filtered.length} total payments`}</div>
                    </div>
                    <button className="pm-create" onClick={() => navigate("/dashboard/payments/create")}>
                        ➕ Create Payment
                    </button>
                </div>

                <div className="pm-filters">
                    <input placeholder="🔍 Search by name, unit, ID..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
                    <select value={projectFilter} onChange={(e) => { setProjectFilter(e.target.value); setPage(1); }}>
                        <option value="">All Projects</option>
                        {projects.map((p) => <option key={p.id} value={p.id}>{p.name || p.shortform}</option>)}
                    </select>
                    <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
                        <option value="">All Status</option>
                        <option value="Confirmed">Confirmed</option>
                        <option value="Received">Received</option>
                    </select>
                    <select value={paymentForFilter} onChange={(e) => { setPaymentForFilter(e.target.value); setPage(1); }}>
                        <option value="">All Types (For)</option>
                        {paymentFors.map((f) => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <select value={modeFilter} onChange={(e) => { setModeFilter(e.target.value); setPage(1); }}>
                        <option value="">Payment Mode</option>
                        {paymentModes.map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <select value={amountSort} onChange={(e) => setAmountSort(e.target.value)}>
                        <option value="">Sort by Amount</option>
                        <option value="asc">Low → High</option>
                        <option value="desc">High → Low</option>
                    </select>
                    <select value={dateSort} onChange={(e) => setDateSort(e.target.value)}>
                        <option value="">Sort by Date</option>
                        <option value="asc">Oldest First</option>
                        <option value="desc">Newest First</option>
                    </select>
                    {(search || statusFilter || projectFilter || paymentForFilter || modeFilter || amountSort || dateSort) && (
                        <button 
                            className="pm-clear-btn" 
                            onClick={() => {
                                setSearch("");
                                setStatusFilter("");
                                setProjectFilter("");
                                setPaymentForFilter("");
                                setModeFilter("");
                                setAmountSort("");
                                setDateSort("");
                                setPage(1);
                                setSearchParams(new URLSearchParams(), { replace: true });
                            }}
                            title="Clear all filters"
                        >
                            ✖ Clear
                        </button>
                    )}
                </div>

                {error && <div className="pm-err">⚠️ {error}</div>}

                {loading ? (
                    <div className="pm-load"><div className="pm-spin" /><div className="pm-load-t">Loading payments...</div></div>
                ) : (
                    <>
                        <div className="pm-wrap">
                            <table className="pm-table">
                                <thead>
                                    <tr>
                                        <th>Customer</th>
                                        <th>Payment ID</th>
                                        <th>Project</th>
                                        <th>Unit</th>
                                        <th>Amount</th>
                                        <th>Status</th>
                                        <th>Mode</th>
                                        <th>For</th>
                                        <th>Collected</th>
                                        <th>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 ? (
                                        <tr><td colSpan="10"><div className="pm-empty">No payments found</div></td></tr>
                                    ) : rows.map((p) => (
                                        <tr key={p.id}>
                                            <td>
                                                <div className="pm-cust">
                                                    <span className="pm-cust-name">{p.customer_name}</span>
                                                    <span className="pm-cust-id">Booking: {p.booking_id}</span>
                                                </div>
                                            </td>
                                            <td style={{ fontWeight: 600, color: "#475569" }}>{p.payment_id}</td>
                                            <td><span className="pm-unit">{projectIdMap[p.project_id] || "—"}</span></td>
                                            <td><span className="pm-unit">{p.unit}</span></td>
                                            <td><span className="pm-amt">{fmt(p.amount)}</span></td>
                                            <td>
                                                <span className={`pm-badge ${p.status === "Confirmed" ? "pm-badge-confirmed" : p.status === "Pending" ? "pm-badge-pending" : "pm-badge-na"}`}>
                                                    {p.status || "—"}
                                                </span>
                                            </td>
                                            <td>{p.payment_mode || "—"}</td>
                                            <td>{p.payment_for || "—"}</td>
                                            <td><span className="pm-date">{p.payment_collect || "—"}</span></td>
                                            <td>
                                                <div className="pm-act-group">
                                                    <button className="pm-view" onClick={() => navigate("/dashboard/payments/view", { state: { payment: p } })}>
                                                        👁️ View
                                                    </button>
                                                    {allowEdit && (
                                                        <button className="pm-edit" onClick={() => navigate("/dashboard/payments/edit", { state: { payment: p } })}>
                                                            ✏️ Edit
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {totalPages > 1 && (
                            <div className="pm-pag">
                                <div className="pm-pag-info">Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, filtered.length)} of {filtered.length}</div>
                                <div className="pm-pag-btns">
                                    <button disabled={page === 1} onClick={() => setPage(1)}>First</button>
                                    <button disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</button>
                                    <span>Page {page} of {totalPages}</span>
                                    <button disabled={page === totalPages} onClick={() => setPage(page + 1)}>Next</button>
                                    <button disabled={page === totalPages} onClick={() => setPage(totalPages)}>Last</button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </>
    );
};

export default Payments;
